import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, UserRound, GraduationCap, CheckSquare, Eye, Users } from "lucide-react";
import { useStaffAuth } from "../context/StaffAuthContext";
import { getMyClassStudents } from "../api/teachersubjects/classTeacherApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Alert,
  Skeleton,
} from "../components/ui";
import { useToast } from "../context/ToastContext";

const STATUS_VARIANTS = {
  ENROLLED: "success",
  LEFT: "danger",
  TRANSFERRED: "warning",
  GRADUATED: "secondary",
};

const MyClass = () => {
  const navigate = useNavigate();
  const { classTeacherSections } = useStaffAuth();
  const [activeSectionId, setActiveSectionId] = useState(
    classTeacherSections[0]?._id || ""
  );
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const loadStudents = async (sectionId) => {
    try {
      setLoading(true);
      const data = await getMyClassStudents(sectionId);
      setStudents(data.students || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSectionId) {
      loadStudents(activeSectionId);
    } else {
      setLoading(false);
    }
  }, [activeSectionId]);

  const handleView = (id) => {
    navigate(`/staff-panel/students/${id}`);
  };

  if (!classTeacherSections.length) {
    return (
      <div className="p-3 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <Card className="border border-dashed border-neutral-200/80 shadow-none text-center p-8 sm:p-12">
          <CardContent className="p-0 space-y-2">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 mx-auto">
              <GraduationCap className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">No Assigned Class Section</p>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
              You are not currently assigned as a class teacher for any active section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div className="flex items-center gap-2.5">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              My Class
            </h1>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
              Manage student records and track daily attendance for your section.
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            leftIcon={<CheckSquare className="w-3.5 h-3.5" />}
            onClick={() =>
              navigate(
                `/staff-panel/my-class/attendance?sectionId=${activeSectionId}`
              )
            }
            className="text-xs"
          >
            Mark Attendance
          </Button>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Eye className="w-3.5 h-3.5" />}
            onClick={() =>
              navigate(
                `/staff-panel/attendance/section?sectionId=${activeSectionId}`
              )
            }
            className="text-xs border-neutral-200 dark:border-neutral-800"
          >
            View Attendance
          </Button>
        </div>
      </div>

      {/* Section Switcher Tabs */}
      {classTeacherSections.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {classTeacherSections.map((sec) => {
            const isActive = activeSectionId === sec._id;
            return (
              <button
                key={sec._id}
                type="button"
                onClick={() => setActiveSectionId(sec._id)}
                className={`whitespace-nowrap rounded-lg px-3.5 py-1.5 text-xs font-medium transition-colors ${ isActive ? "bg-neutral-900 text-white shadow-xs" : "bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200/80" }`}
              >
                Class {sec.classId?.className} - {sec.sectionName}
              </button>
            );
          })}
        </div>
      )}

      {/* Error Alert */}
      

      {/* Main Student Roster Card */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
            Enrolled Students ({students.length})
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              <Skeleton height={40} width="100%" />
              <Skeleton height={40} width="100%" />
              <Skeleton height={40} width="100%" />
            </div>
          ) : students.length === 0 ? (
            <div className="p-8 text-center text-xs text-neutral-400 dark:text-neutral-500">
              No students found in this section.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 text-neutral-500 dark:text-neutral-400 font-medium uppercase text-[10px] tracking-wider">
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Adm. No</th>
                    <th className="px-4 py-3">Class & Section</th>
                    <th className="px-4 py-3">Primary Contact</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200">
                  {students.map((stu) => (
                    <tr key={stu._id} className="hover:bg-neutral-50/50 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
                            {stu.gender === "Female" ? (
                              <UserRound className="w-4 h-4" />
                            ) : (
                              <User className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-xs">
                              {stu.firstName} {stu.middleName} {stu.lastName}
                            </p>
                            <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
                              ID: {stu.studentId}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-2.5 font-medium text-neutral-600 dark:text-neutral-300">
                        {stu.admissionNo}
                      </td>

                      <td className="px-4 py-2.5 text-neutral-600 dark:text-neutral-300">
                        Class {stu.classId?.className} - {stu.sectionId?.sectionName}
                      </td>

                      <td className="px-4 py-2.5 text-neutral-600 dark:text-neutral-300">
                        {stu.primaryContact || "N/A"}
                      </td>

                      <td className="px-4 py-2.5">
                        <Badge variant={STATUS_VARIANTS[stu.status] || "secondary"} className="text-[10px] px-2 py-0.5">
                          {stu.status}
                        </Badge>
                      </td>

                      <td className="px-4 py-2.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleView(stu._id)}
                          className="text-xs h-7 px-2 text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyClass;